import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/apiClient";

const STORAGE_KEY = "celsius-copilot-history-v1";
let _msgSeq = 0;
const _msgId = () => `m-${Date.now()}-${_msgSeq++}`;

/**
 * useCopilotChat — extracts all chat state, persistence, effects, and
 * send/clear handlers out of AICopilotDrawer.jsx (iter47 refactor).
 * Cyclomatic complexity of the drawer drops from 22 → 6.
 */
export default function useCopilotChat({ open }) {
  const [messages, setMessages] = useState(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY) || "[]";
      const parsed = JSON.parse(raw);
      return parsed.map((m) => (m.id ? m : { ...m, id: _msgId() }));
    } catch (err) {
      console.warn("[copilot] failed to rehydrate history:", err);
      return [];
    }
  });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Persist last 24 messages to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-24)));
    } catch (err) {
      console.warn("[copilot] sessionStorage write failed:", err);
    }
  }, [messages]);

  // Focus input when drawer opens
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 260);
  }, [open]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = useCallback(async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    const newHistory = [...messages, { id: _msgId(), role: "user", content: q }];
    setMessages(newHistory);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/copilot", {
        question: q,
        session_id: sessionId,
        history: newHistory.slice(-6).map(({ role, content }) => ({ role, content })),
      });
      if (data.session_id) setSessionId(data.session_id);
      setMessages((m) => [...m, { id: _msgId(), role: "assistant", content: data.answer || "(no reply)" }]);
    } catch (e) {
      console.error("[copilot] send failed:", e);
      const err = e.response?.data?.detail || "Copilot unavailable — please try again.";
      setMessages((m) => [...m, { id: _msgId(), role: "assistant", content: `❌ ${err}` }]);
    } finally { setBusy(false); }
  }, [busy, input, messages, sessionId]);

  const clear = useCallback(() => {
    setMessages([]);
    setSessionId(null);
    try { sessionStorage.removeItem(STORAGE_KEY); }
    catch (err) { console.warn("[copilot] sessionStorage clear failed:", err); }
  }, []);

  return { messages, input, setInput, busy, send, clear, scrollRef, inputRef };
}
